// import type { ShopperCustomers } from '@/scapi';
// import type { LoyaltyData, LoyaltyLevelsResponse } from '@/routes/types/action-responses';
// import { LoyaltyIcon } from './account-loyalty-icon';
import { cn } from '@/lib/utils';
import { Trans, useTranslation } from 'react-i18next';
import { SvgFan } from '../icons-eataly';
import { Badge } from '../ui/badge';
import { useLoyalty, useLoyaltyLoading } from 'src/providers/loyalty';
import { Skeleton } from '../ui/skeleton';

function progressPercent(
    value: number | string,
    min: number | string,
    pointsToNextTier: number | string | undefined
): number {
    const valueNumber = Number(value);
    const minNumber = Number(min);
    const pointsToNextTierNumber = pointsToNextTier ? Number(pointsToNextTier) : null;
    const maxNumber = pointsToNextTierNumber ? valueNumber + pointsToNextTierNumber : null;
    if (maxNumber == null || maxNumber <= minNumber) {
        return 100;
    }
    const raw = ((valueNumber - minNumber) / (maxNumber - minNumber)) * 100;
    return Math.min(100, Math.max(0, raw));
}

// /** API returns levels in arbitrary order; tier math requires ascending minPoint. */
// function levelsByMinPoint(levels: LoyaltyLevelsResponse['levels']) {
//     return [...levels].sort((a, b) => a.minPoint - b.minPoint);
// }
function AccountLoyaltyLevelProgressBarSkeleton({ compact }: { compact?: boolean }) {
    return (
        <div className="flex flex-col gap-4">
            <div className={cn('flex gap-2', compact ? 'items-center justify-between' : 'flex-col gap-6')}>
                <div className="flex gap-2 items-center">
                    <Skeleton className="h-6 w-6" />
                    <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex gap-2 items-center">
                    {!compact && <Skeleton className="h-4 w-24" />}
                    <Skeleton className="h-4 w-24" />
                </div>
            </div>
            <div className="relative h-5 w-full overflow-hidden rounded-full">
                <Skeleton className="h-full rounded-full transition-[width]" />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-base text-foreground">
                <Skeleton className="h-5 w-full" />
            </div>
        </div>
    );
}

export default function AccountLoyaltyLevelProgressBar({ compact }: { compact?: boolean }) {
    const { t } = useTranslation('account');
    const loyaltyData = useLoyalty();
    const isLoading = useLoyaltyLoading();
    if (isLoading) {
        return <AccountLoyaltyLevelProgressBarSkeleton compact={compact} />;
    }
    if (!loyaltyData) {
        return null;
    }
    return (
        <div className="flex flex-col gap-4">
            <div className={cn('flex gap-2', compact ? 'items-center justify-between' : 'flex-col gap-6')}>
                <div className="flex gap-2 items-center">
                    <span className="size-6 shrink-0 rounded-full inline-flex items-center justify-center bg-[var(--brown-20)] text-neutral-white">
                        <SvgFan className="size-5" />
                    </span>
                    <p className="body-copy-l-bold text-primary">{loyaltyData.cardTier}</p>
                </div>
                <div className="flex gap-2 items-center">
                    {!compact && <p className="label-m">{t('loyalty.pointsLabel')}</p>}
                    <Badge hierarchy="primary" schema="warning">
                        {t('loyalty.pointsBalance', { count: loyaltyData.cardBalance })}
                    </Badge>
                </div>
            </div>
            <div
                className="relative h-5 w-full overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-label={t('loyalty.progressAriaLabel')}
                aria-valuemin={loyaltyData.loyaltyTiers[loyaltyData.cardTier] ?? 0}
                aria-valuemax={loyaltyData.loyaltyTiers[loyaltyData.cardNextTier ?? ''] ?? 0}
                aria-valuenow={loyaltyData.cardBalance}>
                <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{
                        width: `max(${progressPercent(loyaltyData.cardBalance, loyaltyData.loyaltyTiers[loyaltyData.cardTier] ?? 0, loyaltyData.cardPointsToNextTier)}%, 20px)`,
                    }}
                />
            </div>
            <div className="flex flex-wrap items-center gap-2 text-base text-foreground">
                {loyaltyData.cardNextTier ? (
                    <p className="body-copy-m flex items-center gap-2 w-full">
                        <span>
                            <Trans
                                ns="account"
                                i18nKey="loyalty.toReachNextLevel"
                                values={{ pointsToNext: loyaltyData.cardPointsToNextTier }}
                                components={{
                                    b: <b className="body-copy-m-bold" />,
                                }}
                            />
                        </span>
                        <span className="inline-flex items-center gap-2 ms-auto text-[var(--brown-30)]">
                            <SvgFan />
                            <span className="body-copy-m-bold">{loyaltyData.cardNextTier}</span>
                        </span>
                    </p>
                ) : (
                    <p>
                        <Trans
                            ns="account"
                            i18nKey="loyalty.reachedHighestTier"
                            values={{ points: loyaltyData.cardBalance }}
                            components={{
                                span: <span className="font-bold" />,
                            }}
                        />
                    </p>
                )}
            </div>
        </div>
    );
}
